import { supabase } from '@/integrations/supabase/client';

// App configuration
export const PRODUCTION_URL = 'https://gistgift.shop';
export const APP_URL = PRODUCTION_URL;

// X OAuth configuration
const X_CLIENT_ID = 'QmZGWHluMUZHbjE5d2M2Z2lPU0U6MTpjaQ'; // X Developer Portal Client ID

// State storage for CSRF protection
const STATE_STORAGE_KEY = 'x_oauth_state';
const STATE_EXPIRY_MS   = 10 * 60 * 1000; // 10 minutes

interface StoredState {
  state: string;
  timestamp: number;
  isNative: boolean;
  redirectUri: string;
  codeVerifier: string; // PKCE code verifier
}

/**
 * Get the appropriate base URL for OAuth redirects.
 * Returns the current origin only when it is a genuine HTTPS production/preview
 * URL. Falls back to PRODUCTION_URL for Capacitor apps and localhost dev servers.
 */
function getBaseUrl(isNative: boolean): string {
  if (isNative && typeof window !== 'undefined') {
    const origin = window.location.origin;
    // FIX: Same bug as tiktok-auth.ts — original checked !origin.includes('localhost:')
    // which let Capacitor's `capacitor://localhost` pass through (no colon after
    // "localhost"), producing an invalid HTTPS redirect URI and a redirect_uri mismatch.
    // Fix: only accept genuine HTTPS URLs with no localhost hostname.
    if (origin && origin.startsWith('https://') && !origin.includes('localhost')) {
      return origin;
    }
  }
  return PRODUCTION_URL;
}

/**
 * Generate a random string for PKCE code verifier
 */
function generateCodeVerifier(): string {
  const array = new Uint8Array(64);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generate code challenge from verifier (SHA-256 hash, base64url encoded)
 */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data    = encoder.encode(verifier);
  const hash    = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(hash));
}

/**
 * Base64 URL encode (RFC 4648)
 */
function base64UrlEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Generate X OAuth URL with PKCE
 */
export async function getXOAuthUrl(isNative: boolean, deeplinkScheme: string = 'gistgift', extraParams?: Record<string, string>): Promise<string> {
  // Generate CSRF state
  const state = crypto.randomUUID();

  // Generate PKCE code verifier and challenge
  const codeVerifier  = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Determine base URL and redirect path based on flow
  const baseUrl      = getBaseUrl(isNative);
  const redirectPath = isNative ? '/native-callback' : '/auth';
  const redirectUri  = `${baseUrl}${redirectPath}`;

  // Store state for validation - include codeVerifier for later exchange
  const storedState: StoredState = {
    state,
    timestamp: Date.now(),
    isNative,
    redirectUri,
    codeVerifier,
  };

  // Try sessionStorage first, fallback to localStorage
  try {
    sessionStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(storedState));
  } catch {
    localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(storedState));
  }

  console.log('[X Auth] Generating OAuth URL');
  console.log('[X Auth] isNative:', isNative);
  console.log('[X Auth] baseUrl:', baseUrl);
  console.log('[X Auth] redirectUri:', redirectUri);

  // FIX: Native state embeds provider tag as a third segment: `uuid|scheme|x`.
  // Previously the format was `uuid|scheme` — identical to TikTok's format — and
  // NativeCallback.tsx relied on a sessionStorage lookup to tell them apart. But
  // NativeCallback runs in the EXTERNAL browser (Safari/Chrome after redirect), which
  // has a completely different storage context from the app's WebView. The stored
  // state was never accessible there, so isXCallback() always returned false on native
  // and every X callback was incorrectly handled as TikTok.
  //
  // With the provider tag embedded in the state string itself, NativeCallback can
  // distinguish providers without any storage access.
  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             X_CLIENT_ID,
    redirect_uri:          redirectUri,
    scope:                 'users.read tweet.read tweet.write offline.access',
    state:                 isNative ? `${state}|${deeplinkScheme}|x` : state,
    code_challenge:        codeChallenge,
    code_challenge_method: 'S256',
  });

  // Append extra query params (e.g. force_login)
  if (extraParams) {
    for (const [key, value] of Object.entries(extraParams)) {
      params.set(key, value);
    }
  }

  return `https://twitter.com/i/oauth2/authorize?${params.toString()}`;
}

/**
 * Validate OAuth state for CSRF protection
 */
export function validateXOAuthState(returnedState: string): {
  valid: boolean;
  isNative: boolean;
  deeplinkScheme?: string;
  redirectUri?: string;
  codeVerifier?: string;
  warning?: string;
} {
  try {
    // Try sessionStorage first, then localStorage
    let stored = sessionStorage.getItem(STATE_STORAGE_KEY);
    if (!stored) {
      stored = localStorage.getItem(STATE_STORAGE_KEY);
    }

    // Parse returned state — format is `uuid` (web) or `uuid|scheme|x` (native).
    // Destructuring takes only the first two parts; the `|x` provider tag is ignored
    // here since it was already used by isXCallback() to route to this handler.
    const [stateValue, deeplinkScheme] = returnedState.split('|');

    if (!stored) {
      console.warn('[X Auth] No stored state found - this is common in native OAuth flows');
      return {
        valid: true,
        isNative: !!deeplinkScheme,
        deeplinkScheme: deeplinkScheme || 'gistgift',
        warning: 'State validation skipped due to storage isolation',
      };
    }

    const storedState: StoredState = JSON.parse(stored);

    // Check expiry
    if (Date.now() - storedState.timestamp > STATE_EXPIRY_MS) {
      console.error('[X Auth] State expired');
      sessionStorage.removeItem(STATE_STORAGE_KEY);
      localStorage.removeItem(STATE_STORAGE_KEY);
      return {
        valid: true,
        isNative: storedState.isNative,
        deeplinkScheme: deeplinkScheme || 'gistgift',
        redirectUri: storedState.redirectUri,
        codeVerifier: storedState.codeVerifier,
        warning: 'State expired but allowing flow to continue',
      };
    }

    // Validate state match
    if (stateValue !== storedState.state) {
      console.error('[X Auth] State mismatch - stored:', storedState.state, 'received:', stateValue);
      return {
        valid: true,
        isNative: storedState.isNative,
        deeplinkScheme: deeplinkScheme || 'gistgift',
        redirectUri: storedState.redirectUri,
        codeVerifier: storedState.codeVerifier,
        warning: 'State mismatch - may be due to native storage isolation',
      };
    }

    // Clear state after successful validation
    sessionStorage.removeItem(STATE_STORAGE_KEY);
    localStorage.removeItem(STATE_STORAGE_KEY);

    return {
      valid: true,
      isNative: storedState.isNative,
      deeplinkScheme: deeplinkScheme || 'gistgift',
      redirectUri: storedState.redirectUri,
      codeVerifier: storedState.codeVerifier,
    };
  } catch (error) {
    console.error('[X Auth] State validation error:', error);
    const [, deeplinkScheme] = returnedState.split('|');
    return {
      valid: true,
      isNative: !!deeplinkScheme,
      deeplinkScheme: deeplinkScheme || 'gistgift',
      warning: 'State validation failed with error',
    };
  }
}

/**
 * Exchange X authorization code for session tokens
 */
export async function exchangeXCode(
  code: string,
  codeVerifier: string,
  isNative: boolean,
  storedRedirectUri?: string
): Promise<{
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    email: string;
    display_name: string;
    avatar_url: string | null;
    x_username: string | null;
  };
}> {
  // Use stored redirectUri if available
  let redirectUri: string;
  if (storedRedirectUri) {
    redirectUri = storedRedirectUri;
    console.log('[X Auth] Using stored redirect_uri:', redirectUri);
  } else {
    const baseUrl      = getBaseUrl(isNative);
    const redirectPath = isNative ? '/native-callback' : '/auth';
    redirectUri = `${baseUrl}${redirectPath}`;
    console.log('[X Auth] Computed redirect_uri:', redirectUri);
  }

  console.log('[X Auth] Exchanging code');
  console.log('[X Auth] isNative:', isNative);
  console.log('[X Auth] redirect_uri:', redirectUri);

  const { data, error } = await supabase.functions.invoke('auth-x-callback', {
    body: { code, code_verifier: codeVerifier, redirect_uri: redirectUri },
  });

  if (error) {
    console.error('[X Auth] Exchange error:', error);
    let errorMessage = error.message || 'Failed to exchange X code';

    if ((error as any).context) {
      try {
        const ctx = (error as any).context;
        if (typeof ctx === 'string') {
          const parsed = JSON.parse(ctx);
          errorMessage = parsed.error || parsed.message || errorMessage;
          if (parsed.details) {
            errorMessage += ` (${JSON.stringify(parsed.details)})`;
          }
        }
      } catch {
        // Ignore parsing errors
      }
    }

    throw new Error(errorMessage);
  }

  if (data?.error) {
    console.error('[X Auth] Exchange returned error:', data);
    let errorMessage = data.error;
    if (data.details) {
      const details = data.details;
      if (details.error_description) {
        errorMessage = details.error_description;
      } else if (details.error) {
        errorMessage = `${data.error}: ${details.error}`;
      }
    }
    if (data.redirect_uri) {
      errorMessage += ` [redirect_uri: ${data.redirect_uri}]`;
    }
    throw new Error(errorMessage);
  }

  if (!data) {
    throw new Error('No data returned from token exchange');
  }

  return data;
}

/**
 * Set Supabase session from X OAuth tokens
 */
export async function setXSession(accessToken: string, refreshToken: string): Promise<void> {
  const { error } = await supabase.auth.setSession({
    access_token:  accessToken,
    refresh_token: refreshToken,
  });

  if (error) {
    console.error('[X Auth] Failed to set session:', error);
    throw error;
  }

  console.log('[X Auth] Session set successfully');
}

/**
 * Check if a callback contains X OAuth params
 */
export function isXCallback(searchParams: URLSearchParams): boolean {
  const hasCode        = searchParams.has('code');
  const hasState       = searchParams.has('state');
  const noAccessToken  = !searchParams.has('access_token');

  if (!hasCode || !hasState || !noAccessToken) return false;

  // FIX: Original relied solely on a sessionStorage lookup to distinguish X from
  // TikTok callbacks. NativeCallback.tsx runs in the EXTERNAL browser after
  // redirect — a completely different storage context from the app's WebView where
  // the state was stored. The lookup always returned null, so isXCallback() always
  // returned false on native, causing every X callback to be processed as TikTok.
  //
  // Fix: check for the provider tag embedded in the state parameter by getXOAuthUrl()
  // (`uuid|deeplinkScheme|x`). This works without any storage access.
  const state    = searchParams.get('state') || '';
  const provider = state.split('|')[2];
  if (provider === 'x') return true;

  // Fallback for web flows (state is just `uuid`, no provider tag) — check storage
  // as before. Web flows don't use NativeCallback, so storage is accessible here.
  try {
    let stored = sessionStorage.getItem(STATE_STORAGE_KEY);
    if (!stored) stored = localStorage.getItem(STATE_STORAGE_KEY);
    if (stored) return true;
  } catch {
    // Ignore storage errors
  }

  return false;
}

/**
 * Get the redirect URI for the current environment
 */
export function getXRedirectUri(isNative: boolean): string {
  const baseUrl = getBaseUrl(isNative);
  return isNative
    ? `${baseUrl}/native-callback`
    : `${baseUrl}/auth`;
}
